import { useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

import {
  FetchListDto,
  GroupDto,
  GroupStatus,
  MessageDto,
  useChatStore,
} from '@/features/chat';

import api from './api/v2';
import { chatKey } from './queryKey';
import { useSearchParams } from 'react-router-dom';

export type UseFetchMessagesParams = {
  groupId: number;
  venueId: number;
  size: number;
};

export type UseFetchMultiChannelMessages = {
  size: number;
  venueIds?: string;
  statuses?: GroupStatus;
};

export function useFetchMultiChannelGroups(
  params: UseFetchMultiChannelMessages,
  options
) {
  const { size, venueIds, statuses } = params;
  const store = useChatStore();
  return useInfiniteQuery<FetchListDto | undefined>(
    chatKey.fetchMultiChannelGroups(venueIds),
    async function ({ pageParam = 0 }) {
      try {
        const { data } = await api.get<FetchListDto>(
          `/chatting/groups?page=${pageParam}&size=${size}&venueIds=${venueIds}`
        );
        if (data.content.length > 0) {
          store.setGroupMapFromData(data.content as GroupDto[]);
          if (store.currentGroup.id === -1 && !store.newChatStarted) {
            store.setDefaultSelectedGroup();
          }
        }
        return data;
      } catch (error) {
        // TODO: error처리
      }
    },
    {
      getNextPageParam: (lastPage: any) => {
        if (lastPage?.last) {
          return undefined;
        } else {
          return lastPage?.pageable?.pageNumber + 1 || undefined;
        }
      },
      useErrorBoundary: true,
      suspense: true,
      ...options,
    }
  );
}

export function useFetchMessages(
  params: UseFetchMessagesParams,
  options?: any
) {
  const { venueId, groupId, size } = params;
  const store = useChatStore();
  return useInfiniteQuery<FetchListDto>({
    queryKey: chatKey.fetchMessages(groupId),
    async queryFn({ pageParam = 0 }) {
      try {
        const { data } = await api.get(
          `/venues/${venueId}/messages?groupId=${groupId}&page=${pageParam}&size=${size}`
        );
        store.setMessageMapFromData(data.content as MessageDto[]);
        return data;
      } catch (error) {
        console.log(error);
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      else if (lastPage.last) return undefined;
      return lastPage.pageable.pageNumber + 1;
    },
    keepPreviousData: true,
    useErrorBoundary: true,
    suspense: true,
    enabled: groupId !== -1 && venueId !== -1,
    ...options,
  });
}

export type UseFetchGroupsParams = {
  size: number;
  venueId: number;
};

export type UseFetchHasUnreadMessageParams = {
  venueId?: string;
};
export function useFetchHasUnreadMessage(
  params: UseFetchHasUnreadMessageParams,
  options?: any
) {
  const { venueId } = params;
  return useQuery({
    queryKey: chatKey.fetchHasUnreadMessage(venueId),
    async queryFn() {
      try {
        const { data } = await api.get(`venues/${venueId}/messages/unread`);
        return data;
      } catch (err) {
        console.log(err);
      }
    },
    refetchInterval: 1000 * 30 * 1,
    ...options,
  });
}

export function useFetchNewMessages(params: UseFetchMessagesParams) {
  const { venueId, groupId, size } = params;
  const store = useChatStore();
  return useQuery<FetchListDto>({
    queryKey: chatKey.fetchNewMessages(groupId),
    async queryFn() {
      try {
        const { data } = await api.get(
          `/venues/${venueId}/messages?groupId=${groupId}&page=0&size=${size}`
        );
        // 안읽은 메시지가 fetch된 경우
        if (
          store.lastMessageId &&
          data?.sort.sorted &&
          data.content.length !== 0 &&
          data.content.sort(
            (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
          )?.[0].id !== store.lastMessageId
        ) {
          store.setMessageMapFromData(data.content as MessageDto[]);
        }
        return data;
      } catch (err) {
        console.log(err);
      }
    },
    suspense: true,
    enabled: groupId !== -1 && venueId !== -1,
  });
}

export function useFetchGroups(params: UseFetchGroupsParams, options) {
  const { size, venueId } = params;
  const [searchParams] = useSearchParams();
  const store = useChatStore();
  return useInfiniteQuery<FetchListDto | undefined>(
    chatKey.fetchGroups(venueId),
    async function ({ pageParam = 0 }) {
      try {
        const { data } = await api.get<FetchListDto>(
          `/venues/${venueId}/groups?page=${pageParam}&size=${size}`
        );
        if (data.content.length > 0) {
          store.setGroupMapFromData(data.content as GroupDto[]);
          if (store.currentGroup.id === -1 && !store.newChatStarted) {
            if (searchParams.get('groupId')) {
              store.setDefaultSelectedGroup(
                Number(searchParams.get('groupId'))
              );
            } else {
              store.setDefaultSelectedGroup();
            }
          }
        }
        return data;
      } catch (error) {
        // TODO: error처리
      }
    },
    {
      getNextPageParam: (lastPage: any) => {
        if (lastPage?.last) {
          return undefined;
        } else {
          return lastPage?.pageable?.pageNumber + 1 || undefined;
        }
      },
      useErrorBoundary: true,
      suspense: true,
      ...options,
      // enabled: !!venueId,
    }
  );
}

export function usePollGroupUpdates(
  params: { venueIds?: string },
  options?: any
) {
  const store = useChatStore();
  const sinceRef = useRef<string>(new Date().toISOString().replace('Z', ''));

  return useQuery<GroupDto[]>({
    queryKey: chatKey.fetchGroupUpdates(params.venueIds),
    async queryFn() {
      const { data } = await api.get<GroupDto[]>(
        `/chatting/groups/updates?since=${sinceRef.current}${params.venueIds ? `&venueIds=${params.venueIds}` : ''}`
      );
      if (data.length > 0) {
        store.setGroupMapFromData(data);
        const maxAt = data.reduce(
          (max, g) => (g.at > max ? g.at : max),
          data[0].at
        );
        sinceRef.current = maxAt;
      }
      return data;
    },
    refetchInterval: 30_000,
    enabled: store.groupList.length > 0,
    ...options,
  });
}

export type UsePostMessageParams = {
  groupId: number | null;
  content: string;
  fileIds: number[];
  tempMessageId: number;
};

export function usePostMessage(
  venueId: number,
  onSuccess?: (res) => void,
  onError?: (error: V2ErrorType) => void
) {
  return useMutation(
    async (values: UsePostMessageParams) => {
      const { tempMessageId, ...payload } = values;
      const { data } = await api.post<MessageDto>(
        `/venues/${venueId}/messages`,
        payload
      );
      Object.assign(data, { tempMessageId });
      return data;
    },
    {
      onSuccess,
      onError,
      networkMode: 'always',
    }
  );
}

export type UseEditMessageParams = {
  venueId: number;
  messageId: number;
  groupId: number;
  content: string;
  fileIds: number[];
};

export function useEditMessage(
  onSuccess?: (res) => void,
  onError?: (error: V2ErrorType) => void
) {
  return useMutation(
    (values: UseEditMessageParams) => {
      const { venueId, messageId, ...rest } = values;
      return api.put(`venues/${venueId}/messages/${messageId}`, rest);
    },
    {
      onSuccess,
      onError,
    }
  );
}

export type UseReadMessageParams = {
  groupId: number;
  lastMessageId: number;
};

export function useReadMessage(
  venueId: number,
  onSuccess?: (res) => void,
  onError?: (error: V2ErrorType) => void
) {
  return useMutation(
    (values: UseReadMessageParams) =>
      api.post<MessageDto>(`/venues/${venueId}/messages/read`, values),
    {
      onSuccess,
      onError,
    }
  );
}

export type UseUpdateGroupStatusParams = {
  groupId: number;
  venueId: number;
  onSuccess: (res) => void;
  onError: (err) => void;
};

export function useUpdateToClosed(params: UseUpdateGroupStatusParams) {
  const { groupId, venueId, onSuccess, onError } = params;
  return useMutation(
    () => api.post(`/venues/${venueId}/groups/${groupId}/close`),
    {
      onSuccess,
      onError,
    }
  );
}

export function useUpdateToProceeding(params: UseUpdateGroupStatusParams) {
  const { groupId, venueId, onSuccess, onError } = params;
  return useMutation(
    () => api.post(`/venues/${venueId}/groups/${groupId}/proceed`),
    {
      onSuccess,
      onError,
    }
  );
}

export function useUpdateGroupTitle({
  onSuccess,
  onError,
}: {
  onSuccess: (res) => void;
  onError: (err) => void;
}) {
  return useMutation(
    (values: { venueId: number; groupId: number; title: string }) => {
      const { venueId, groupId, ...rest } = values;
      return api.patch(`/venues/${venueId}/groups/${groupId}`, rest);
    },
    {
      onSuccess,
      onError,
    }
  );
}

export type FetchFilteredGroupsParams = {
  venueIds?: string;
  statuses?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
};

export async function fetchFilteredGroups(
  params: FetchFilteredGroupsParams
): Promise<FetchListDto> {
  const { venueIds, statuses, fromDate, toDate, page = 0, size = 100 } = params;
  const queryParams = new URLSearchParams();
  queryParams.set('page', String(page));
  queryParams.set('size', String(size));
  if (venueIds) queryParams.set('venueIds', venueIds);
  if (statuses) queryParams.set('statuses', statuses);
  if (fromDate) queryParams.set('fromDate', fromDate);
  if (toDate) queryParams.set('toDate', toDate);

  const { data } = await api.get<FetchListDto>(
    `/chatting/groups?${queryParams.toString()}`
  );
  return data;
}

export type FetchAllMessagesParams = {
  venueId: number;
  groupId: number;
};

export async function fetchAllMessages(
  params: FetchAllMessagesParams
): Promise<MessageDto[]> {
  const { venueId, groupId } = params;
  const { data } = await api.get<FetchListDto>(
    `/venues/${venueId}/messages?groupId=${groupId}&page=0&size=1000`
  );
  return data.content as MessageDto[];
}

export async function fetchBulkMessages(
  groupIds: number[]
): Promise<Record<number, MessageDto[]>> {
  const { data } = await api.post<Record<number, MessageDto[]>>(
    '/chatting/messages/bulk',
    { groupIds }
  );
  return data;
}
